import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ShieldCheck, Landmark } from "lucide-react";

const cls = "mt-1 border-zinc-800 bg-[#2F0D18] text-zinc-100";

export function VerifyCard() {
  const { refresh } = useAuth();
  const [f, setF] = useState({ pan_number: "", instagram_handle: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/organizer/verify", f); toast.success("You're verified — go publish your first event"); await refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} data-testid="verify-card" className="mb-8 rounded-3xl border border-punch-500/40 bg-punch-500/5 p-6 glow-punch">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-punch-400"><ShieldCheck className="h-3.5 w-3.5" /> One-time verification</div>
      <h2 className="mt-1 font-display text-2xl font-extrabold text-zinc-50">Prove you're legit before you publish</h2>
      <p className="mt-1 text-sm text-zinc-400">Keeps fans safe from scams. Only the Afterhours owner sees this.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <div><Label className="text-xs uppercase tracking-widest text-zinc-400">PAN number</Label><Input data-testid="verify-pan" required maxLength={10} placeholder="ABCDE1234F" value={f.pan_number} onChange={(e) => setF({ ...f, pan_number: e.target.value.toUpperCase() })} className={`${cls} font-mono uppercase`} /></div>
        <div><Label className="text-xs uppercase tracking-widest text-zinc-400">Instagram</Label><Input data-testid="verify-instagram" required placeholder="@yourcrew" value={f.instagram_handle} onChange={(e) => setF({ ...f, instagram_handle: e.target.value })} className={cls} /></div>
        <div><Label className="text-xs uppercase tracking-widest text-zinc-400">Phone</Label><Input data-testid="verify-phone" placeholder="+91" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={cls} /></div>
      </div>
      <Button data-testid="verify-submit" type="submit" disabled={saving} className="mt-4 bg-punch-500 font-bold text-white hover:bg-punch-600">{saving ? "Verifying…" : "Verify me"}</Button>
    </form>
  );
}

export function PayoutAccountCard({ account, onSaved }) {
  const [f, setF] = useState({ account_holder: account?.account_holder || "", bank_account: account?.bank_account || "", ifsc: account?.ifsc || "" });
  const [open, setOpen] = useState(!account?.bank_account);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { const { data } = await api.put("/organizer/payout-account", f); toast.success("Payout account saved"); onSaved(data); setOpen(false); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="payout-account-card" className="mb-8 rounded-2xl border border-zinc-800/80 bg-[#3A1020] p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-amber-400"><Landmark className="h-3.5 w-3.5" /> Payout bank account</div>
          <div className="mt-1 text-sm text-zinc-300">{account?.bank_account ? <>{account.account_holder} · ••••{account.bank_account.slice(-4)} · {account.ifsc}{account.razorpay_account_id && <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 font-mono text-[10px] text-amber-400">UPI split linked</span>}</> : "Add your bank so Wednesday payouts land automatically."}</div>
        </div>
        {!open && <Button data-testid="payout-account-edit" size="sm" variant="ghost" onClick={() => setOpen(true)} className="text-zinc-300 hover:text-amber-400">Edit</Button>}
      </div>
      {open && (
        <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-[1fr,1fr,auto,auto]">
          <Input data-testid="payout-holder" required placeholder="Account holder name" value={f.account_holder} onChange={(e) => setF({ ...f, account_holder: e.target.value })} className={cls} />
          <Input data-testid="payout-account" required placeholder="Account number" value={f.bank_account} onChange={(e) => setF({ ...f, bank_account: e.target.value })} className={`${cls} font-mono`} />
          <Input data-testid="payout-ifsc" required placeholder="IFSC" value={f.ifsc} onChange={(e) => setF({ ...f, ifsc: e.target.value.toUpperCase() })} className={`${cls} w-32 font-mono uppercase`} />
          <Button data-testid="payout-save" type="submit" disabled={saving} className="mt-1 bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">{saving ? "Saving…" : "Save"}</Button>
        </form>
      )}
    </div>
  );
}
